/**
 * KanbanColumn Component
 *
 * Column in the Kanban board for a specific request status.
 * Supports drag-and-drop via @dnd-kit.
 *
 * @module components/dev/KanbanColumn
 */

import React from 'react';
import { Box, Paper, Typography, Badge, Stack } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RequestCard from './RequestCard';
import { FeatureRequest, RequestStatus, STATUS_BADGES } from '../../types/featureRequest';

interface KanbanColumnProps {
  status: RequestStatus;
  requests: FeatureRequest[];
  count: number;
}

/**
 * SortableRequestCard - Wrapper for draggable request card
 */
const SortableRequestCard: React.FC<{ request: FeatureRequest }> = ({ request }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: request.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Box mb={2}>
        <RequestCard request={request} isDragging={isDragging} />
      </Box>
    </div>
  );
};

/**
 * KanbanColumn - Status column for requests
 */
const KanbanColumn: React.FC<KanbanColumnProps> = ({ status, requests, count }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const statusConfig = STATUS_BADGES[status];

  return (
    <Paper
      ref={setNodeRef}
      elevation={isOver ? 8 : 2}
      sx={{
        p: 2,
        minHeight: 600,
        bgcolor: isOver ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s',
        borderTop: `4px solid ${statusConfig.color}`,
      }}
    >
      {/* Column Header */}
      <Box mb={2}>
        <Badge
          badgeContent={count}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              right: -3,
              top: 3,
            },
          }}
        >
          <Typography variant="h6" component="div">
            {statusConfig.icon} {statusConfig.label}
          </Typography>
        </Badge>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {statusConfig.description}
        </Typography>
      </Box>

      {/* Request Cards */}
      <SortableContext
        items={requests.map((req) => req.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack spacing={2}>
          {requests.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Drop requests here</Typography>
            </Box>
          ) : (
            requests.map((request) => (
              <SortableRequestCard key={request.id} request={request} />
            ))
          )}
        </Stack>
      </SortableContext>
    </Paper>
  );
};

export default KanbanColumn;
